import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Layout, Popover, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { managementOp } from 'store/ducks/Management';
import { RootState } from 'store/configureStore';
import { TitleListEN, TitleLists } from 'store/ducks/globalTypes';
import {
  IDataManagementLayoutProps,
  TitleKey
} from 'store/ducks/Management/types';
import { OptionType } from 'store/ducks/Global/types';
import { complianceEstOp } from 'store/ducks/ComplienceEst';
import CustomSelect from 'components/CustomSelect';
import Helpers from 'utils/Helpers';
import TitleBar from 'components/TitleBar';
import GoBackButton from 'components/GoBackButton';
import ApiService from 'utils/ApiCalls';
import { globalOp } from 'store/ducks/Global';
import * as Styled from 'components/Layouts/Layout.styled';

declare global {
  interface Window {
    hasUnsavedChanges?: boolean;
    showUnsavedChangesModal?: () => Promise<boolean>;
  }
}

const { Sider } = Layout;
const { useToken } = theme;
const { Content } = Layout;

const DataManagementLayout: React.FC<IDataManagementLayoutProps> = ({
  activeTab,
  activeKey,
  children
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { token } = useToken();
  const { t } = useTranslation();
  const [moduleOptionsState, setModuleOptionsState] = useState<OptionType[]>(
    []
  );
  const [groupOptions, setGroupOptions] = useState<
    { label: string; value: number }[]
  >([]);

  const { lang, saveDisabled, drawTable, scheme, orgContext } = useSelector(
    (state: RootState) => ({
      lang: state.global.lang,
      drawTable: state.management.drawTable,
      saveDisabled: state.management.saveDisabled,
      scheme: state.global.scheme,
      orgContext: state.global.hierarchicalLevelsScope
    })
  );
  const selectedGroupValues = orgContext?.group ?? [];
  const page = location.pathname.split('/').pop();

  const titleKey: TitleKey = (() => {
    switch (page) {
      case 'auditFindings':
        return 'AUDIT_FINDINGS';
      case 'category':
        return 'CATEGORY';
      case 'product':
        return 'PRODUCT';
      case 'solutionType':
        return 'SOLUTION_TYPE';
      case 'taskType':
        return 'TASK_TYPE';
      default:
        return 'AUDIT_FINDINGS';
    }
  })();

  const safeModuleArray = Array.isArray(orgContext.module)
    ? orgContext.module
    : orgContext.module !== undefined && orgContext.module !== null
    ? [orgContext.module]
    : [];

  const selectedModuleLabels = safeModuleArray
    .map((m: any) => {
      if (typeof m === 'object' && m !== null) {
        return m.label;
      }

      const option = moduleOptionsState.find((o) => o.value === m);
      return option?.label ?? null;
    })
    .filter(Boolean);

  const titles = TitleLists[lang] || TitleListEN;

  const applyFilters = async () => {
    const baseParams = Helpers.toApiFilters(orgContext);

    const filterParams = {
      ...baseParams,
      module: selectedModuleLabels
    };

    const hasFilters = Object.keys(filterParams).length > 0;
    if (!orgContext || Object.keys(orgContext).length === 0) return;

    if (!hasFilters) return;

    switch (page) {
      case 'auditFindings':
        managementOp.fetchMgmtAudits(lang, filterParams).then((data) => {
          dispatch(managementOp.setMgmtAudits(data));
        });
        break;

      case 'category':
        managementOp.fetchCategory(lang, filterParams).then((data) => {
          dispatch(managementOp.setCategory(data));
        });
        break;

      case 'product':
        managementOp.fetchProduct(lang, filterParams).then((data) => {
          dispatch(managementOp.setProduct(data));
        });
        break;

      default:
        return;
    }

    dispatch(managementOp.setDrawTable(true));

    localStorage.setItem('orgContext', JSON.stringify(orgContext));
  };

  useEffect(() => {
    applyFilters();
  }, []);

  const handleSave = () => {
    dispatch(managementOp.setSaveClicked(true));
  };

  useEffect(() => {
    dispatch(managementOp.setFilters(lang));
  }, []);

  /* ---------------- fetch modules ---------------- */
  useEffect(() => {
    const fetchModules = async () => {
      try {
        const moduleGroups = Array.isArray(orgContext.moduleGroup)
          ? orgContext.moduleGroup
          : orgContext.moduleGroup
          ? [orgContext.moduleGroup]
          : [];

        if (!moduleGroups.length) {
          setModuleOptionsState([]);
          return;
        }

        const ids = moduleGroups.map((item: any) =>
          typeof item === 'object' ? item.value : item
        );

        const res = await ApiService.getModulesByModuleGroups({
          module_group_id: ids
        });

        const modules = res?.data?.data ?? [];

        const unique = Array.from(
          new Map(
            modules
              .filter((m: any) => m?.mod_id != null)
              .map((m: any) => [m.mod_id, m])
          ).values()
        );

        setModuleOptionsState(
          unique.map((m: any) => ({
            value: m.mod_id,
            label: m.mod_short_name
          }))
        );
      } catch (err) {
        console.error('Failed to fetch modules:', err);
        setModuleOptionsState([]);
      }
    };

    fetchModules();
  }, [JSON.stringify(orgContext.moduleGroup)]);

  const selectedModuleValues = Array.isArray(orgContext.module)
    ? orgContext.module.map((m: any) => m.value ?? m)
    : [];

  useEffect(() => {
    dispatch(complianceEstOp.setDrawTable(false));
  }, [activeTab, activeKey]);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await ApiService.getGroupBPR();

        const data = response.data;
        const groups = Array.isArray(data)
          ? data
          : data.groups ?? data.data ?? [];

        setGroupOptions(
          groups.map((item: any) => ({
            label: item.name ?? item.group_name ?? item.label,
            value: item.id ?? item.group_id ?? item.value
          }))
        );
      } catch (error) {
        console.error('Failed to fetch groups:', error);
      }
    };

    fetchGroups();
  }, []);

  return (
    <Styled.StyledLayout token={token}>
      <TitleBar
        title={titles[titleKey]}
        goBackButtons={
          <>
            <GoBackButton
              to={
                page !== 'auditFindings'
                  ? '/data_management_workflow'
                  : '/workingAreaDetails/audit-findings'
              }
              text={t('GO_BACK')}
              checkUnsavedChanges={true}
            />
            <GoBackButton
              to='/working_area'
              text={t('GO_TO_WORKING_AREAS')}
              checkUnsavedChanges={true}
            />
          </>
        }
      />
      <Styled.HorizontalFilterBar token={token} scheme={scheme}>
        <Styled.FiltersContainer>
          <Styled.FiltersSubtitle>
            {t('SELECTED_FOCUS_AREAS', {
              defaultValue: 'Selected Focus Areas'
            })}
          </Styled.FiltersSubtitle>
          <Styled.FilterTextItem>
            <Styled.FilterLabel>{t('MODULE')}</Styled.FilterLabel>
            <Styled.FilterValue>
              {(Array.isArray(selectedModuleValues)
                ? selectedModuleValues
                : [selectedModuleValues]
              )
                .filter((value) => value !== '' && value != null)
                .map(
                  (value) =>
                    moduleOptionsState.find((option) => option.value === value)
                      ?.label ?? value
                )
                .join(', ') || '-'}
            </Styled.FilterValue>
          </Styled.FilterTextItem>

          <Styled.FilterTextItem>
            <Styled.FilterLabel>{t('GROUP')}</Styled.FilterLabel>
            <Styled.FilterValue>
              {(Array.isArray(selectedGroupValues)
                ? selectedGroupValues
                : [selectedGroupValues]
              )
                .filter((value) => value !== '' && value != null)
                .map(
                  (value) =>
                    groupOptions.find((option) => option.value === value)
                      ?.label ?? value
                )
                .join(', ') || '-'}
            </Styled.FilterValue>
          </Styled.FilterTextItem>
        </Styled.FiltersContainer>
        <Styled.ActionButtons
          isDataManagementPage={true}
          hasDisplayedData={drawTable}
        >
          <Styled.ApplyButton
            type='primary'
            onClick={applyFilters}
            scheme={scheme}
          >
            {t('DISPLAY')}
          </Styled.ApplyButton>
          {drawTable && (
            <>
              <Styled.ApplyButton
                disabled={saveDisabled}
                onClick={handleSave}
                scheme={scheme}
              >
                {t('SAVE')}
              </Styled.ApplyButton>
            </>
          )}
        </Styled.ActionButtons>
      </Styled.HorizontalFilterBar>
      <Styled.StyledLayout token={token}>
        <Layout>
          <Content>{children}</Content>
        </Layout>
        <Sider />
      </Styled.StyledLayout>
    </Styled.StyledLayout>
  );
};

export default DataManagementLayout;
